import { useQuery } from '@apollo/client';
import { Flex, Spinner, Stack } from '@chakra-ui/react';
import { partADeductible, partBDeductible } from '@components/crm/utils/irmaa';
import Highlight from '@components/features/Highlight';
import Button, { ButtonVariants } from '@components/interactive/Button';
import Body1 from '@components/typography/Body1';
import { getPlansForMsWizard } from '@gql/GetPlansForMsWizard';
import { useRouter } from 'next/router';
import Container from './Container';
import PlanTypeCard from './PlanTypeCard';

const PlanTypes = ({ wizardState, goNext }) => {
  const router = useRouter();

  const { data, loading } = useQuery(getPlansForMsWizard, {
    variables: {
      ...wizardState,
    },
  });

  const onSelect = (planType) => goNext({ ...data.getPlansForMsWizard, planType });

  const isLoading = loading || !data.getPlansForMsWizard;

  return (
    <Container
      header={isLoading ? 'Searching for plans in your area...' : 'Which plan fits your needs?'}
      w={['100%', '100%', '100%', '900px']}
      maxW={['100%', '100%', '100%', '900px']}
      px={[4, 4, 16, 0]}
    >
      {isLoading ? (
        <Flex justifyContent="center" py={12}>
          <Spinner />
        </Flex>
      ) : (
        <>
          <Highlight
            maxW="500px"
            textAlign="center"
            text="In general we recommend Plan G [+] because it has the most comprehensive coverage."
            queries={[
              {
                key: 'Plan G [+]',
                description: `Plan G covers your $${partADeductible} Part A deducible, your 20% Part B coinsurance, and your copay on doctor visits. The most you’ll pay out-of-pocket for Medicare approved medical expenses is your Part B deductible: $${partBDeductible}/year. And because Plan G benefits are regulated, the best option is the carrier with the lowest cost.`,
              },
            ]}
            renderText={(value) => <Body1>{value}</Body1>}
          />
          <Stack w="100%" mx={[4, 4, 'auto']} alignItems="center" pt={[2, 2, 4, 8]} spacing={4}>
            <Flex w="100%" gap={10} flexDir={['column', 'column', 'column', 'row']}>
              <PlanTypeCard
                planType="G"
                bullets={[
                  'Most comprehensive',
                  'Medical costs capped at $233 per year for providers and procedures covered by Medicare.',
                ]}
                plans={data.getPlansForMsWizard.planG}
                onSelect={onSelect}
              />
              <PlanTypeCard
                planType="N"
                bullets={[
                  '$20 co-pay for primary care visits',
                  'Up to 15% of medical bill in "excess charges" for some providers.',
                ]}
                plans={data.getPlansForMsWizard.planN}
                onSelect={onSelect}
              />
            </Flex>
            <Stack w="100%" alignItems="center" pt={4} gap={4} spacing={0}>
              <Button type="button" onClick={router.back} variant={ButtonVariants.tertiary} w="100%">
                Back
              </Button>
            </Stack>
          </Stack>
        </>
      )}
    </Container>
  );
};

export default PlanTypes;
